export const PROGRAM_LABEL = {
  retail: "Retail", retail_traditional: "Retail · Traditional", retail_acquisition: "Retail · Acquisition",
  associate: "Associate Agent", protege: "Protégé", agency_producer: "Agency Producer", producer: "Agency Producer",
};

export const programLabel = (enr) => {
  if (!enr) return null;
  const base = PROGRAM_LABEL[enr.program] || enr.program;
  const sub = enr.program_subtype ? ` · ${enr.program_subtype.charAt(0).toUpperCase()}${enr.program_subtype.slice(1)}` : "";
  return `${base}${sub}${enr.version_label ? ` — ${enr.version_label}` : ""}`;
};

export const ENDED_LABEL = { completed: "Program Completed", withdrawn: "Withdrawn", terminated: "Terminated", ended: "Program Ended", inactive: "Program Ended" };
export const isEnded = (enr) => !!(enr && ENDED_LABEL[enr.status]);

export const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const WAITING_STATUSES = new Set(["ready_for_review", "blocked"]);

const OPEN = (t) => !t.is_done && !t.is_planned;
export const splitTasks = (tasks = []) => ({
  mine: tasks.filter((t) => OPEN(t) && t.owner === "candidate" && !SUBMITTED.has(t.status)),
  attention: tasks.filter((t) => OPEN(t) && t.owner === "candidate" && t.status === "needs_attention"),
  submitted: tasks.filter((t) => OPEN(t) && t.owner === "candidate" && t.status === "submitted"),
  district: tasks.filter((t) => OPEN(t) && t.owner === "district"),
  later: tasks.filter((t) => !t.is_done && t.is_planned && t.owner === "candidate"),
  done: tasks.filter((t) => t.is_done && t.status !== "cancelled" && t.owner === "candidate"),
});
const SUBMITTED = new Set(["submitted", "needs_attention"]);
export const titleOf = (t) => t.display_title || t.title;
export const STATUS_LABEL = { not_started: "Not Started", in_progress: "In Process", submitted: "Submitted — In Review", needs_attention: "Needs Attention", completed: "Completed", planned: "Upcoming", cancelled: "Cancelled" };

export const taskCounts = (tasks = [], owner) => {
  const own = tasks.filter((t) => t.owner === owner && t.status !== "cancelled");
  return { done: own.filter((t) => t.is_done).length, total: own.length };
};

export const waitingItems = (tasks = [], roadmap) => [
  ...((roadmap?.requirements) || []).filter((r) => WAITING_STATUSES.has(r.status))
    .map((r) => ({ id: `r-${r.requirement_id}`, title: r.title, sub: r.status === "ready_for_review" ? "In review with your district team" : "Waiting on a district step" })),
  ...splitTasks(tasks).submitted.map((t) => ({ id: t.id, title: titleOf(t), sub: "You submitted this — in review with your district team" })),
  ...splitTasks(tasks).district.map((t) => ({ id: t.id, title: t.title, sub: t.staff_held ? t.owner_label : `Assigned to ${t.assignee_name || t.owner_label} · ${t.status === "in_progress" ? "In Process" : "Not Started"}` })),
];

const phaseIndex = (phases, name) => { const i = phases.findIndex((p) => p.name === name); return i < 0 ? 999 : i; };

// Deterministic priority order — first matching rule wins. Planned/upcoming tasks are never the next step.
export function nextBestAction({ roadmap, tasks = [], upcoming }) {
  const enr = roadmap?.enrollment;
  if (isEnded(enr)) return { kind: "ended", title: ENDED_LABEL[enr.status], detail: "Your program is no longer active. Your completed history and documents remain available. Contact your district team with any questions.", cta: "Contact Your Team", go: ["support", "team"] };

  const phases = roadmap?.phases || [];
  const current = phases.find((p) => p.is_current);
  const mine = [...splitTasks(tasks).mine].sort((a, b) =>
    phaseIndex(phases, a.phase) - phaseIndex(phases, b.phase) || (a.due_date || "9999").localeCompare(b.due_date || "9999"));
  const taskAction = (t, why) => ({ kind: "task", title: titleOf(t), detail: t.why_it_matters || why, cta: t.needs_submission ? "Upload Document" : "View Task", go: ["tasks", null, { taskId: t.id }] });

  const blocking = mine.find((t) => current && t.phase === current.name);
  if (blocking) return taskAction(blocking, `This is required to move forward in ${current.name}.`);
  if (mine[0]) return taskAction(mine[0], mine[0].due_date ? `Due ${fmtDate(mine[0].due_date)}.` : "This is your next task.");

  const attnTask = splitTasks(tasks).attention[0];
  if (attnTask) return { kind: "attention", title: `Needs attention: ${titleOf(attnTask)}`, detail: attnTask.candidate_feedback || "Your district team needs an update from you on this item.", cta: "Upload Replacement", go: ["tasks", null, { taskId: attnTask.id }] };
  const attention = (roadmap?.requirements || []).find((r) => r.status === "action_needed");
  if (attention) return { kind: "attention", title: `Needs attention: ${attention.title}`, detail: attention.candidate_instructions || "Your district team needs an update from you on this item.", cta: attention.document_required ? "Upload Document" : "View in My Journey", go: attention.document_required ? ["documents", "docs"] : ["journey", "roadmap"] };

  if (upcoming?.start && (new Date(upcoming.start) - Date.now()) < 72 * 3600 * 1000) return { kind: "appointment", title: `Get ready for your ${upcoming.appointment_type}`, detail: `${new Date(upcoming.start).toLocaleString()} with ${upcoming.host_name || "your team"}.`, cta: upcoming.meeting_link ? "Join Meeting" : "View Appointment", href: upcoming.meeting_link, go: ["appointments", "upcoming"] };

  const step = (roadmap?.next_actions || []).find((r) => !r.gated && r.document_required && r.status !== "ready_for_review");
  if (step) return { kind: "upload", title: `Upload: ${step.title}`, detail: step.candidate_instructions || "Your district team needs this document to keep your program moving.", cta: "Upload Document", go: ["documents", "docs"] };

  if (!enr && !upcoming) return { kind: "schedule", title: "Meet with your district team", detail: "Schedule time with your recruiter to talk through your path and next steps.", cta: "Book a Meeting", go: ["appointments", "book"] };

  const w = waitingItems(tasks, roadmap)[0];
  return { kind: "waiting", title: "You're caught up", detail: w ? `Your district team is currently working on: ${w.title}. We'll let you know when your next step is ready.` : "There are no actions waiting on you right now. We'll let you know when your next step is ready.", cta: "Contact Your Team", go: ["support", "team"] };
}

export const candidateActionCount = (tasks = []) => { const s = splitTasks(tasks); return s.mine.length + s.attention.length; };
